import { GridColDef } from '@mui/x-data-grid-pro';
import { getDriveMonitorLogs, UserKeys } from '../../../api/users-api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { DriveMonitorJobLog } from '../../../../types';
import { Messages } from '../../../utils/message-constants';
import { useState } from 'react';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import StatusLabel from '../../../shared/status-label';
import Box from '@mui/material/Box';
import List from '@mui/icons-material/List';

interface DriveSweepLogsGridProps {
  folderId: string;
  height?: number;
}

export default function DriveSweepLogsGrid({
  folderId,
  height = 400,
}: Readonly<DriveSweepLogsGridProps>) {
  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
  }>({
    pageSize: 200,
    page: 0,
    cursor: null,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.DriveSweepLogs, folderId, paginationModel],
    queryFn: () =>
      getDriveMonitorLogs(
        {
          results: [],
          firstPull: paginationModel.page === 0,
          searchTime: 0,
          pageSize: paginationModel.pageSize,
          cursor: paginationModel.cursor,
          nameSpace: '',
        },
        folderId
      ),
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('drive sweep logs'),
    },
  });

  const columns: GridColDef<DriveMonitorJobLog>[] = [
    {
      field: 'dateOfRun',
      headerName: 'Run Time',
      editable: false,
      sortable: false,
      width: 180,
      type: 'dateTime',
      valueGetter: (value: number) => new Date(value),
      valueFormatter: (params: Date) => params.toLocaleString(),
    },
    {
      field: 'successful',
      headerName: 'Successful Run',
      editable: false,
      sortable: false,
      flex: 1,
      type: 'boolean',
    },
    {
      field: 'jobStatus',
      headerName: 'Job Status',
      editable: false,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        const statusValue = params.row.jobStatus?.toUpperCase() ?? 'UNKNOWN';
        const status =
          statusValue === 'PROCESSING'
            ? 'PROCESSING'
            : statusValue === 'LOCKED'
            ? 'LOCKED'
            : statusValue === 'COMPLETE'
            ? 'COMPLETED'
            : 'UNKNOWN';
        return <StatusLabel status={status} />;
      },
    },
    {
      field: 'successCount',
      headerName: 'Successful Documents',
      editable: false,
      sortable: false,
      width: 180,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'errorCount',
      headerName: 'Errored Documents',
      editable: false,
      sortable: false,
      width: 180,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  return (
    <Box sx={{ height }}>
      <SharedDataGridServer
        showToolbar={false}
        slots={{
          ...createGridOverlays('Logs', <List fontSize="large" />),
        }}
        pageSizeOptions={[200]}
        getRowId={(row) => row.dateOfRun}
        disableRowSelectionOnClick
        disableColumnMenu
        columns={columns}
        data={data}
        loading={isLoading || isFetching}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (isFetching || isLoading) return;
          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
            });
          } else {
            setPaginationModel({ ...newModel, cursor: data?.cursor || '' });
          }
        }}
      />
    </Box>
  );
}
